// Belge önizleme: pdf.js ile canvas çizimi + araç çubuğu (yakınlaştır, döndür, sayfa, indir, yazdır, tam ekran)
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { toast } from 'sonner';
import {
  Loader2, ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCcw, RotateCw, ChevronUp, ChevronDown, Download, Printer, MoveHorizontal,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

const ToolBtn = ({ onClick, disabled, title, testId, children }) => (
  <button
    type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
    className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-200 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
    data-testid={testId}
  >
    {children}
  </button>
);

const Sep = () => <span className="mx-1 h-5 w-px bg-white/15" />;

// Çizilmiş sayfaları (canvas / img) yazdırma penceresine aktarır
const printImages = (sources) => {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<html><head><style>@page{margin:8mm}body{margin:0}img{display:block;max-width:100%;max-height:100vh;margin:0 auto;page-break-after:always}</style></head><body>${sources.map(s => `<img src="${s}"/>`).join('')}</body></html>`);
  doc.close();
  const imgs = Array.from(doc.images);
  Promise.all(imgs.map(i => (i.complete ? null : new Promise(r => { i.onload = r; i.onerror = r; })))).then(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  });
};

export const DocumentPreview = ({ url, contentType, filename, alt }) => {
  const isPdf = contentType === 'application/pdf';
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const [baseWidth, setBaseWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pages, setPages] = useState(0);
  const [current, setCurrent] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [natural, setNatural] = useState(null);

  const measure = useCallback(() => {
    if (scrollRef.current) setBaseWidth(scrollRef.current.clientWidth - 32);
  }, []);

  useEffect(() => {
    measure();
    const onFs = () => { setFullscreen(document.fullscreenElement === wrapRef.current); setTimeout(measure, 50); };
    document.addEventListener('fullscreenchange', onFs);
    window.addEventListener('resize', measure);
    return () => { document.removeEventListener('fullscreenchange', onFs); window.removeEventListener('resize', measure); };
  }, [measure]);

  const width = Math.max(200, Math.round(baseWidth * zoom));
  const zoomIdx = ZOOM_STEPS.indexOf(zoom);
  const stepZoom = (dir) => {
    const idx = zoomIdx === -1 ? ZOOM_STEPS.findIndex(z => z >= zoom) : zoomIdx;
    setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, idx + dir))]);
  };
  const rotate = (deg) => setRotation(r => (r + deg + 360) % 360);

  const pageEls = () => Array.from(scrollRef.current?.querySelectorAll('[data-page-index]') || []);
  const goTo = (n) => {
    const el = pageEls()[n - 1];
    if (el) { scrollRef.current.scrollTo({ top: el.offsetTop - 12, behavior: 'smooth' }); setCurrent(n); }
  };
  const onScroll = () => {
    if (!isPdf) return;
    const top = scrollRef.current.scrollTop + scrollRef.current.clientHeight / 3;
    const idx = pageEls().filter(el => el.offsetTop <= top).length;
    if (idx && idx !== current) setCurrent(idx);
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await wrapRef.current.requestFullscreen();
    } catch {
      toast.error('Tarayıcınız bu ekranda tam ekrana izin vermiyor');
    }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'belge';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const print = () => {
    if (isPdf) {
      const canvases = Array.from(scrollRef.current?.querySelectorAll('canvas') || []);
      if (!canvases.length) return;
      printImages(canvases.map(c => c.toDataURL('image/png')));
    } else {
      const img = scrollRef.current?.querySelector('img');
      if (!img) return;
      const c = document.createElement('canvas');
      const swap = rotation % 180 !== 0;
      c.width = swap ? img.naturalHeight : img.naturalWidth;
      c.height = swap ? img.naturalWidth : img.naturalHeight;
      const ctx = c.getContext('2d');
      ctx.translate(c.width / 2, c.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      printImages([c.toDataURL('image/png')]);
    }
  };

  const swapped = rotation % 180 !== 0;

  return (
    <div ref={wrapRef} className={`w-full flex flex-col rounded-lg overflow-hidden border border-slate-200 bg-slate-100 ${fullscreen ? 'h-screen' : 'h-[55dvh] sm:h-[65vh]'}`} data-testid="document-preview">
      <div className="flex items-center gap-0.5 px-2 py-1 bg-slate-800 text-slate-200 flex-wrap" data-testid="document-preview-toolbar">
        <ToolBtn onClick={() => stepZoom(-1)} disabled={zoom <= ZOOM_STEPS[0]} title="Uzaklaştır" testId="preview-zoom-out"><ZoomOut className="h-4 w-4" /></ToolBtn>
        <span className="min-w-12 text-center text-xs tabular-nums" data-testid="preview-zoom-level">{Math.round(zoom * 100)}%</span>
        <ToolBtn onClick={() => stepZoom(1)} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} title="Yakınlaştır" testId="preview-zoom-in"><ZoomIn className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => setZoom(1)} title="Sayfaya sığdır" testId="preview-fit"><MoveHorizontal className="h-4 w-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => rotate(-90)} title="Sola döndür" testId="preview-rotate-left"><RotateCcw className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => rotate(90)} title="Sağa döndür" testId="preview-rotate-right"><RotateCw className="h-4 w-4" /></ToolBtn>
        {isPdf && (
          <>
            <Sep />
            <ToolBtn onClick={() => goTo(current - 1)} disabled={current <= 1} title="Önceki sayfa" testId="preview-prev-page"><ChevronUp className="h-4 w-4" /></ToolBtn>
            <span className="min-w-12 text-center text-xs tabular-nums" data-testid="preview-page-indicator">{pages ? `${current} / ${pages}` : '-'}</span>
            <ToolBtn onClick={() => goTo(current + 1)} disabled={current >= pages} title="Sonraki sayfa" testId="preview-next-page"><ChevronDown className="h-4 w-4" /></ToolBtn>
          </>
        )}
        <span className="flex-1" />
        <ToolBtn onClick={download} title="İndir" testId="preview-download"><Download className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={print} title="Yazdır" testId="preview-print"><Printer className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={toggleFullscreen} title={fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'} testId="preview-fullscreen">
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </ToolBtn>
      </div>

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto relative" data-testid={isPdf ? 'pdf-preview' : 'image-preview'}>
        {isPdf ? (
          <Document
            file={url}
            onLoadSuccess={({ numPages }) => { setPages(numPages); setCurrent(1); }}
            loading={<div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
            error={<p className="text-sm text-rose-500 p-4" data-testid="pdf-preview-error">PDF görüntülenemedi, lütfen indirerek açın</p>}
            className="flex flex-col items-center gap-3 p-4 w-max min-w-full"
          >
            {baseWidth > 0 && Array.from({ length: pages }, (_, i) => (
              <div key={i} data-page-index={i} className="shadow bg-white">
                <Page pageNumber={i + 1} width={width} rotate={rotation} renderTextLayer={false} renderAnnotationLayer={false} />
              </div>
            ))}
          </Document>
        ) : (
          <div className="p-4 w-max min-w-full flex justify-center">
            {/* Döndürmede kutu boyutu da döner; görsel her zaman seçili genişliğe sığar */}
            <div className="relative" style={natural ? { width, height: swapped ? width * natural.w / natural.h : width * natural.h / natural.w } : undefined}>
              <img
                src={url} alt={alt}
                onLoad={(e) => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                style={natural ? {
                  position: 'absolute', left: '50%', top: '50%', maxWidth: 'none',
                  width: swapped ? width * natural.w / natural.h : width,
                  transform: `translate(-50%, -50%) rotate(${rotation}deg)`, transition: 'transform 150ms ease',
                } : { visibility: 'hidden' }}
                className="shadow bg-white"
                data-testid="image-preview-img"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
